import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'react-toastify';
import { 
    ChevronUpIcon, 
    ClipboardDocumentIcon, 
    ArrowUturnLeftIcon,
    ClockIcon,
    TruckIcon,
    CheckBadgeIcon,
    XCircleIcon 
} from '@heroicons/react/20/solid';
import { Disclosure, Transition } from '@headlessui/react';
import api from '../utils/api';
import PasswordChangeForm from '../components/PasswordChangeForm';
import AddressesPage from './AddressesPage';
import OrdersPage from './OrdersPage';
import Modal from '../components/Modal';
import CampaignHistoryTab from '../components/CampaignHistoryTab';
import ReturnsTab from '../components/ReturnsTab';

const ProfileInfoPanel = () => {
    const { user, token } = useAuth();
    const [loading, setLoading] = useState(true);
    
    // Form state'leri
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [gender, setGender] = useState('');
    const [birthDay, setBirthDay] = useState('');
    const [birthMonth, setBirthMonth] = useState('');
    const [birthYear, setBirthYear] = useState('');

    const fetchProfile = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const data = await api(`/api/profile/me`, { 
                headers: { 'Authorization': `Bearer ${token}` } 
            });
            if (data) {
                const nameParts = (data.full_name || '').split(' ');
                setFirstName(nameParts.slice(0, -1).join(' '));
                setLastName(nameParts.slice(-1).join(' '));
                setPhone(data.phone || '');
                
                // --- YENİ: Veritabanından gelen verileri state'e aktarma ---
                setGender(data.gender || '');
                if (data.birth_date) {
                    const date = new Date(data.birth_date);
                    setBirthDay(date.getUTCDate());
                    setBirthMonth(date.getUTCMonth() + 1);
                    setBirthYear(date.getUTCFullYear());
                }
            }
        } catch (error) {
            toast.error("Profil bilgileri yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);
    
    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        // --- YENİ: Tarih verisini birleştirip payload'a ekleme ---
        let birth_date = null;
        if (birthYear && birthMonth && birthDay) {
            // Tarihin YYYY-MM-DD formatında olduğundan emin oluyoruz.
            const month = String(birthMonth).padStart(2, '0');
            const day = String(birthDay).padStart(2, '0');
            birth_date = `${birthYear}-${month}-${day}`;
        }

        const payload = {
            full_name: `${firstName} ${lastName}`.trim(),
            phone: phone,
            gender: gender,
            birth_date: birth_date
        };

        try {
            await api('/api/profile', {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(payload)
            });
            toast.success("Bilgileriniz başarıyla güncellendi.");
        } catch(err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const days = Array.from({ length: 31 }, (_, i) => i + 1);
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    const years = Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i);

    if (loading) return (
        <div className="flex justify-center items-center h-64">
            <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200"></div>
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-green border-t-transparent absolute top-0 left-0"></div>
            </div>
        </div>
    )

    return (
        <div className="animate-fade-in">
            <div className="flex items-center mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-brand-green to-brand-green-light rounded-xl flex items-center justify-center shadow-lg mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                </div>
                <div>
                    <h3 className="text-2xl font-bold text-text-dark">Kullanıcı Bilgilerim</h3>
                    <p className="text-text-light text-sm">Kişisel bilgilerinizi güncelleyin</p>
                </div>
            </div>
            <form onSubmit={handleProfileUpdate}>
                <div className="p-8 border-2 border-gray-100 rounded-3xl mb-8 bg-gradient-to-br from-brand-header to-gray-50 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center mb-6">
                        <div className="w-10 h-10 bg-brand-green rounded-xl flex items-center justify-center mr-3">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                        </div>
                        <div>
                            <h4 className="font-bold text-xl text-gray-800">İletişim Bilgileri</h4>
                            <p className="text-sm text-gray-600">Telefon numaranızı buradan güncelleyebilirsiniz</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="group">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Cep Telefonu</label>
                            <div className="relative">
                                <input 
                                    type="tel" 
                                    value={phone} 
                                    onChange={e => setPhone(e.target.value)} 
                                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-brand-green focus:ring-opacity-20 focus:border-brand-green transition-all duration-300 bg-white"
                                    placeholder="0555 123 45 67"
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                                    <svg className="w-5 h-5 text-brand-green group-focus-within:text-brand-green-light transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                        <div className="group">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">E-Posta</label>
                            <div className="relative">
                                <input 
                                    type="email" 
                                    value={user?.email || ''} 
                                    disabled 
                                    className="w-full px-4 py-4 border-2 border-gray-200 bg-gray-100 cursor-not-allowed rounded-2xl text-gray-500"
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-8 border-2 border-gray-100 rounded-3xl mb-8 bg-gradient-to-br from-gray-50 to-brand-header hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center mb-6">
                        <div className="w-10 h-10 bg-brand-green rounded-xl flex items-center justify-center mr-3">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </div>
                        <div>
                            <h4 className="font-bold text-xl text-gray-800">Üyelik Bilgilerim</h4>
                            <p className="text-sm text-gray-600">Temel hesap bilgileriniz</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="group">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Adı</label>
                            <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-brand-green focus:ring-opacity-20 focus:border-brand-green transition-all duration-300 bg-white" />
                        </div>
                         <div className="group">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Soyadı</label>
                            <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-brand-green focus:ring-opacity-20 focus:border-brand-green transition-all duration-300 bg-white" />
                        </div>
                    </div>
                </div>

                <div className="p-8 border-2 border-gray-100 rounded-3xl mb-8 bg-gradient-to-br from-brand-header to-gray-50 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center mb-6">
                        <div className="w-10 h-10 bg-brand-green rounded-xl flex items-center justify-center mr-3">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <div>
                            <h4 className="font-bold text-xl text-gray-800">Opsiyonel Bilgiler</h4>
                            <p className="text-sm text-gray-600">İsteğe bağlı kişisel bilgileriniz</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="group">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Cinsiyet</label>
                            <select value={gender} onChange={e => setGender(e.target.value)} className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-brand-green focus:ring-opacity-20 focus:border-brand-green transition-all duration-300 bg-white">
                                <option value="">Seçiniz</option>
                                <option value="male">Erkek</option>
                                <option value="female">Kadın</option>
                            </select>
                        </div>
                        <div className="group">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Doğum Tarihi</label>
                            <div className="flex gap-3">
                                <select value={birthDay} onChange={e => setBirthDay(e.target.value)} className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-brand-green focus:ring-opacity-20 focus:border-brand-green transition-all duration-300 bg-white"><option value="">Gün</option>{days.map(d => <option key={d} value={d}>{d}</option>)}</select>
                                <select value={birthMonth} onChange={e => setBirthMonth(e.target.value)} className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-brand-green focus:ring-opacity-20 focus:border-brand-green transition-all duration-300 bg-white"><option value="">Ay</option>{months.map(m => <option key={m} value={m}>{m}</option>)}</select>
                                <select value={birthYear} onChange={e => setBirthYear(e.target.value)} className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-brand-green focus:ring-opacity-20 focus:border-brand-green transition-all duration-300 bg-white"><option value="">Yıl</option>{years.map(y => <option key={y} value={y}>{y}</option>)}</select>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="flex justify-end">
                    <button type="submit" disabled={loading} className="bg-gradient-to-r from-brand-green to-brand-green-light hover:from-brand-green-light hover:to-brand-green text-white font-bold py-4 px-10 rounded-2xl disabled:opacity-50 transform hover:scale-[1.02] transition-all duration-300 shadow-xl hover:shadow-2xl border-2 border-transparent hover:border-white/20">
                        {loading ? (
                            <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Kaydediliyor...
                            </span>
                        ) : 'Değişiklikleri Kaydet'}
                    </button>
                </div>
            </form>
        </div>
    );
};

// İade Ödeme Bilgileri Paneli
const PaymentInfoPanel = () => {
    const { token } = useAuth();
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [newPaymentMethod, setNewPaymentMethod] = useState({
        type: 'iban',
        title: '',
        details: '',
        is_default: false
    });

    const fetchPaymentMethods = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const data = await api.get('/api/profile/payment-methods', { 
                headers: { Authorization: `Bearer ${token}` } 
            });
            setPaymentMethods(data || []);
        } catch (error) {
            console.error('Ödeme yöntemleri getirilemedi:', error);
            setPaymentMethods([]);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchPaymentMethods();
    }, [fetchPaymentMethods]);

    const handleAddPaymentMethod = async (e) => {
        e.preventDefault();
        if (!newPaymentMethod.title.trim() || !newPaymentMethod.details.trim()) {
            toast.warn('Lütfen tüm alanları doldurun.');
            return;
        }

        setLoading(true);
        try {
            await api.post('/api/profile/payment-methods', newPaymentMethod, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success('Ödeme yöntemi başarıyla eklendi.');
            setNewPaymentMethod({ type: 'iban', title: '', details: '', is_default: false });
            setIsAddingNew(false);
            fetchPaymentMethods();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Ödeme yöntemi eklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePaymentMethod = async (id) => {
        if (!window.confirm('Bu ödeme yöntemini silmek istediğinizden emin misiniz?')) return;
        
        try {
            await api.delete(`/api/profile/payment-methods/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Ödeme yöntemi silindi.');
            fetchPaymentMethods();
        } catch (error) {
            toast.error('Ödeme yöntemi silinirken bir hata oluştu.');
        }
    };

    const handleSetDefault = async (id) => {
        try {
            await api.put(`/api/profile/payment-methods/${id}/set-default`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Varsayılan ödeme yöntemi güncellendi.');
            fetchPaymentMethods();
        } catch (error) {
            toast.error('Varsayılan ödeme yöntemi güncellenirken bir hata oluştu.');
        }
    };

    if (loading && !isAddingNew) return (
        <div className="flex justify-center items-center h-64">
            <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200"></div>
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-green border-t-transparent absolute top-0 left-0"></div>
            </div>
        </div>
    );

    return (
        <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-brand-green to-brand-green-light rounded-xl flex items-center justify-center shadow-lg mr-4">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-text-dark">İade Ödeme Bilgilerim</h3>
                        <p className="text-text-light text-sm">İade işlemlerinde kullanılacak ödeme bilgilerinizi yönetin</p>
                    </div>
                </div>
                <button
                    onClick={() => setIsAddingNew(true)}
                    className="bg-brand-green text-white px-4 py-2 rounded-xl font-medium hover:bg-brand-green-light transition-colors"
                >
                    + Yeni Ekle
                </button>
            </div>

            {/* Yeni Ödeme Yöntemi Ekleme Formu */}
            {isAddingNew && (
                <div className="mb-6 p-6 border-2 border-brand-green rounded-3xl bg-gradient-to-br from-brand-header to-gray-50">
                    <h4 className="font-bold text-xl text-gray-800 mb-4">Yeni Ödeme Yöntemi Ekle</h4>
                    <form onSubmit={handleAddPaymentMethod} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Ödeme Türü</label>
                                <select
                                    value={newPaymentMethod.type}
                                    onChange={(e) => setNewPaymentMethod({...newPaymentMethod, type: e.target.value})}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-brand-green focus:ring-opacity-20 focus:border-brand-green transition-all duration-300"
                                >
                                    <option value="iban">IBAN</option>
                                    <option value="papara">Papara</option>
                                    <option value="ininal">İninal</option>
                                    <option value="other">Diğer</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Başlık</label>
                                <input
                                    type="text"
                                    value={newPaymentMethod.title}
                                    onChange={(e) => setNewPaymentMethod({...newPaymentMethod, title: e.target.value})}
                                    placeholder="Örn: Ana IBAN Hesabım"
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-brand-green focus:ring-opacity-20 focus:border-brand-green transition-all duration-300"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Ödeme Detayları</label>
                            <textarea
                                value={newPaymentMethod.details}
                                onChange={(e) => setNewPaymentMethod({...newPaymentMethod, details: e.target.value})}
                                rows={3}
                                placeholder="Örn: TR12 3456 7890 1234 5678 90 (Ahmet Yılmaz - Ziraat Bankası)"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-brand-green focus:ring-opacity-20 focus:border-brand-green transition-all duration-300"
                            />
                        </div>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="is_default"
                                checked={newPaymentMethod.is_default}
                                onChange={(e) => setNewPaymentMethod({...newPaymentMethod, is_default: e.target.checked})}
                                className="rounded border-gray-300 text-brand-green focus:ring-brand-green"
                            />
                            <label htmlFor="is_default" className="ml-2 text-sm text-gray-700">
                                Varsayılan ödeme yöntemi olarak ayarla
                            </label>
                        </div>
                        <div className="flex gap-3">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 bg-brand-green text-white py-3 rounded-xl font-medium hover:bg-brand-green-light disabled:bg-gray-400 transition-colors"
                            >
                                {loading ? 'Ekleniyor...' : 'Ödeme Yöntemi Ekle'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsAddingNew(false)}
                                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-300 transition-colors"
                            >
                                İptal
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Mevcut Ödeme Yöntemleri */}
            <div className="space-y-4">
                {paymentMethods.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-3xl">
                        <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-600 mb-2">Henüz Ödeme Yöntemi Yok</h3>
                        <p className="text-gray-500">İade işlemlerinde kullanmak üzere ödeme bilgilerinizi ekleyin.</p>
                    </div>
                ) : (
                    paymentMethods.map((method, index) => (
                        <div key={method.id || index} className="p-6 border-2 border-gray-100 rounded-3xl bg-white hover:shadow-lg transition-all duration-300">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center mb-2">
                                        <span className="px-3 py-1 bg-brand-green text-white rounded-full text-xs font-medium mr-3">
                                            {method.type?.toUpperCase() || 'IBAN'}
                                        </span>
                                        <h4 className="font-semibold text-gray-800">{method.title}</h4>
                                        {method.is_default && (
                                            <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                                Varsayılan
                                            </span>
                                        )}
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                                        <p className="text-gray-700 whitespace-pre-wrap">{method.details}</p>
                                    </div>
                                </div>
                                <div className="flex gap-2 ml-4">
                                    {!method.is_default && (
                                        <button
                                            onClick={() => handleSetDefault(method.id)}
                                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 transition-colors"
                                        >
                                            Varsayılan Yap
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleDeletePaymentMethod(method.id)}
                                        className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-sm hover:bg-red-200 transition-colors"
                                    >
                                        Sil
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

// Ana AccountPage Component'i aynı kalıyor, değişiklik yok.
function AccountPage() {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Parse tab from URL query params
    const queryParams = new URLSearchParams(location.search);
    const initialTab = queryParams.get('tab') || 'profile';
    
    const [activeTab, setActiveTab] = useState(initialTab);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState(null);
    const [orderToReturn, setOrderToReturn] = useState(null);

    useEffect(() => {
        const tab = queryParams.get('tab');
        if (tab) {
            setActiveTab(tab);
        }
    }, [location.search]);

    const openModal = (content) => {
        setModalContent(content);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setModalContent(null);
    };

    const menuItems = [
        { id: 'profile', label: 'Kullanıcı Bilgilerim' },
        { id: 'address', label: 'Adres Bilgilerim' },
        { id: 'orders', label: 'Siparişlerim' },
        { id: 'returns', label: 'İadelerim' },
        { id: 'campaigns', label: 'Kampanya Geçmişim' },
        { id: 'payment', label: 'İade Ödeme Bilgilerim' },
        { id: 'password', label: 'Şifre Değiştirme' },
    ];

    const renderContent = () => {
        switch (activeTab) {
            case 'profile':
                return <ProfileInfoPanel />;
            case 'address':
                return <AddressesPage openModal={openModal} closeModal={closeModal} />;
            case 'password':
                return <PasswordChangeForm />;
            case 'orders':
                return (
                    <div>
                        <OrdersPage 
                            embedded={true} 
                            openModal={openModal} 
                            closeModal={closeModal} 
                            onInitiateReturn={(order) => {
                                setOrderToReturn(order);
                                setActiveTab('returns');
                                // Optionally update URL without reloading
                                navigate('/my-account?tab=returns', { replace: true });
                            }}
                        />
                    </div>
                );
            case 'returns':
                return (
                    <ReturnsTab 
                        orderToReturn={orderToReturn} 
                        onReturnCreated={() => {
                            setOrderToReturn(null);
                        }}
                        onCancelReturnCreation={() => {
                            setOrderToReturn(null);
                            setActiveTab('orders');
                            navigate('/my-account?tab=orders', { replace: true });
                        }}
                    />
                );
            case 'campaigns':
                return <CampaignHistoryTab />;
            case 'payment':
                return <PaymentInfoPanel />;
            default:
                return <ProfileInfoPanel />;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Modern Header */}
                <div className="text-center mb-12 animate-fade-in">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-green rounded-full mb-4 shadow-lg">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold text-text-dark mb-3 tracking-tight">Hesabım</h1>
                    <p className="text-text-light text-lg max-w-md mx-auto">Profil bilgilerinizi yönetin ve siparişlerinizi takip edin</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* Modern Sidebar */}
                    <aside className="lg:col-span-1">
                        <div className="bg-brand-header rounded-3xl shadow-xl p-6 border border-gray-100 backdrop-blur-sm sticky top-8">
                            <nav className="space-y-2">
                                {menuItems.map((item, index) => (
                                    <button
                                        key={item.id}
                                        onClick={() => {
                                            setActiveTab(item.id);
                                            navigate(`/my-account?tab=${item.id}`, { replace: true });
                                            if (item.id !== 'returns') setOrderToReturn(null);
                                        }}
                                        className={`group w-full text-left px-6 py-4 rounded-2xl font-medium transition-all duration-300 transform hover:scale-[1.02] ${
                                            activeTab === item.id 
                                            ? 'bg-gradient-to-r from-brand-green to-brand-green-light text-white shadow-lg ring-4 ring-brand-green ring-opacity-20' 
                                            : 'text-gray-700 hover:bg-gray-50 hover:shadow-md'
                                        }`}
                                        style={{ animationDelay: `${index * 0.1}s` }}
                                    >
                                        <div className="flex items-center">
                                            <div className={`w-3 h-3 rounded-full mr-3 transition-all duration-300 ${
                                                activeTab === item.id ? 'bg-white' : 'bg-gray-300 group-hover:bg-brand-green'
                                            }`}></div>
                                            {item.label}
                                        </div>
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </aside>

                    {/* Modern Content Area */}
                    <main className="lg:col-span-3">
                        <div className="bg-brand-header rounded-3xl shadow-2xl p-8 border border-gray-100 backdrop-blur-sm min-h-[600px]">
                            {renderContent()}
                        </div>
                    </main>
                </div>
            </div>
            <Modal isOpen={isModalOpen} onClose={closeModal}>
                {modalContent}
            </Modal>
        </div>
    );
}

export default AccountPage;